import { ReactNode, useCallback, useMemo, useState } from 'react';
import { ZodError } from 'zod';
import { ToastQueueContext, ToastQueueItem, ToastSeverity } from './toast-queue-context';

interface ToastQueueProviderProps {
  children: ReactNode;
}

function unique<T>(values: T[]): T[] {
  return Array.from(new Set(values));
}

function formatZodIssuePath(path: (string | number)[]): string {
  if (!path.length) return '';
  return path.join('.');
}

export function ToastQueueProvider({ children }: ToastQueueProviderProps) {
  const [toasts, setToasts] = useState<ToastQueueItem[]>([]);

  const pushToast = useCallback((message: string, severity: ToastSeverity = 'error') => {
    if (!message) {
      return;
    }

    if (severity === 'error') {
      console.error('Toast Error:', message);
    }

    setToasts((prev) => {
      // Collapse identical, still-queued toasts into one to reduce UI noise.
      const isDuplicate = prev.some(
        (toast) => toast.message === message && toast.severity === severity,
      );

      if (isDuplicate) return prev;
      return [...prev, { id: Math.random().toString(36).slice(2, 11), message, severity }];
    });
  }, []);

  const dismissToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((err) => err.id !== id));
  }, []);

  const clearAll = useCallback(() => {
    setToasts([]);
  }, []);

  const value = useMemo(() => {
    function pushZodErrors(error: ZodError) {
      const messages = unique(
        error.issues.map((issue) => {
          const safePath = issue.path.filter(
            (p): p is string | number => typeof p === 'string' || typeof p === 'number',
          );
          const at = formatZodIssuePath(safePath);
          return at ? `${issue.message} (${at})` : issue.message;
        }),
      );

      if (messages.length === 0) {
        pushToast('Validation failed', 'error');
        return;
      }

      // Avoid spamming; show up to 5 issues.
      messages.slice(0, 5).forEach((m) => pushToast(m, 'error'));
      if (messages.length > 5) {
        pushToast(`+${messages.length - 5} more validation errors`, 'error');
      }
    }

    return {
      toasts,
      pushToast,
      dismissToast,
      clearAll,
      pushZodErrors,
    };
  }, [toasts, pushToast, dismissToast, clearAll]);

  return <ToastQueueContext.Provider value={value}>{children}</ToastQueueContext.Provider>;
}
